import { fetchWrapper } from './apiBase';
import { stringify } from 'querystring';

import { IRepositoryFileRequest } from '@/types';
import { BASE_API_URL } from '@/config/environment';

const DEFAULT_PAGE_SIZE = 100;

import { IDataFrameResponse, IDataFrameField } from './dataFrameTypes';

export type TDFRequestQueryParams = {
  query_id?: string;
  sort_by?: string;
  filter?: string;
  randomize?: string;
  reverse?: 'true' | 'false' | undefined;
  page?: string;
  page_size?: string | number;
  view?: string;
  columns?: string[];
  rowId?: string;
  sql?: string;
};

export const isStringField = (field: IDataFrameField) => {
  return field.dtype === 'str';
};

export const isNumberField = (field: IDataFrameField) => {
  return ['u16', 'u32', 'u64', 'i8', 'i16', 'i32', 'i64', 'f32', 'f64'].includes(field.dtype);
};

export const isIntField = (field: IDataFrameField) => {
  return ['u16', 'u32', 'u64', 'i8', 'i16', 'i32', 'i64'].includes(field.dtype);
};

export const isFloatField = (field: IDataFrameField) => {
  return ['f32', 'f64'].includes(field.dtype);
};

export const isStructField = (field: IDataFrameField) => {
  return field.dtype.startsWith('struct[');
};

export const isListField = (field: IDataFrameField) => {
  return field.dtype.startsWith('list[');
};

const removeEmptyAttributes = (obj: Object) => {
  return Object.entries(obj)
    .filter(([_, v]) => v != null && v !== undefined && v !== '')
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
};

export const buildQueryObject = (queryOptions: TDFRequestQueryParams): TDFRequestQueryParams => {
  // console.log('buildQueryObject > queryOptions', queryOptions);
  const queryObject: TDFRequestQueryParams = {
    sort_by: queryOptions.sort_by,
    reverse: queryOptions.reverse,
    query_id: queryOptions.query_id,
    randomize: queryOptions.randomize,
    page: queryOptions.page,
    page_size: queryOptions.page_size,
    view: queryOptions.view,
    filter: queryOptions.filter,
    columns: queryOptions.columns,
    sql: queryOptions.sql,
  };
  const cleanQueryObject = removeEmptyAttributes(queryObject);
  // console.log('buildQueryObject > cleanQueryObject', cleanQueryObject);
  return cleanQueryObject;
};

const getRepositoryDfPath = (fileInfo: IRepositoryFileRequest): string => {
  const { namespace, repositoryName, resource } = fileInfo;
  return `/repos/${namespace}/${repositoryName}/data_frames/${resource}`;
};

export const fetchDataFrameStructure = async (
  fileInfo: IRepositoryFileRequest,
  token?: string,
): Promise<IDataFrameResponse> => {
  const { namespace, repositoryName } = fileInfo;
  const targetUrl = `${BASE_API_URL}${getRepositoryDfPath(fileInfo)}?${stringify({
    page: 1,
    page_size: 1,
  })}`;

  return fetchWrapper({
    resourceName: 'dataFrameStructure',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};

export const fetchDataFrame = async (
  fileInfo: IRepositoryFileRequest,
  query: TDFRequestQueryParams,
  token?: string,
): Promise<IDataFrameResponse> => {
  const { namespace, repositoryName } = fileInfo;
  const queryWithDefaultPageSize = { ...query, page_size: query.page_size || DEFAULT_PAGE_SIZE };
  // console.log('fetchDataFrame > query', query);
  const targetUrl = `${BASE_API_URL}${getRepositoryDfPath(fileInfo)}?${stringify(queryWithDefaultPageSize as any)}`;
  console.log('fetchDataFrame > targetUrl', targetUrl);

  return fetchWrapper({
    resourceName: 'dataFrame',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    extra: {
      query: query,
    },
  });
};

export const fetchDataFrameByPath = async (
  namespace: string,
  repositoryName: string,
  derivedResourcePath: string,
  query: TDFRequestQueryParams,
  token?: string,
): Promise<IDataFrameResponse> => {
  const queryWithDefaultPageSize = { ...query, page_size: DEFAULT_PAGE_SIZE };
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}${derivedResourcePath}?${stringify(
    queryWithDefaultPageSize as any,
  )}`;
  // console.log('fetchDataFrameByPath > targetUrl', targetUrl);

  return fetchWrapper({
    resourceName: 'dataFrame',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    extra: {
      query: query,
    },
  });
};
